import { ReactNode, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconoirNavArrowDown, IconoirNavArrowUp } from "@ey-xd/motif-icon";
import {
  MotifIcon,
  MotifProgressLoader,
  MotifErrorMessage,
} from "@ey-xd/motif-react";
import { IconoirArrowLeft } from "@ey-xd/motif-icon";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import Banner from "@/components/Banner/Banner";
import Table from "@/components/Table/table";
import { usePaymentSummaryColumnDefs } from "../summaryTable/PaymentSummaryColumnDefs";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import {
  useGetPaymentSettlementListQuery,
  useGetPaymentSummaryQuery,
} from "@/services/paymentStatus/paymentStatusReducer";
import CustomPagination from "@/components/Pagination/CustomPagination";
import { useDispatch, useSelector } from "react-redux";
import { ActiveTabsOrder } from "@/menuData";
import { GetCommonDownloadImgButton } from "@/components/Images/CommonImages";
import { useLazyDownloadDocumentQuery } from "@/services/documents/documentsReducer";
import { dateFunc } from "@/components/Functions/dateFunc";
import {
  convertToCommaSeparatedNumberwithoutDecimal,
  formatAmountWithCommas,
  getPaymentStatus,
} from "@/utility/common";
import PaymentStatusColorCode from "../PaymentStatusColorCode";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
const PaymentSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId, paymentId } = useParams();
  const retainState = useSelector(
    (e: any) => e.retainState.paymentSummaryTabState
  );
  console.log("retainState", retainState);
  const dispatch = useDispatch();

  const { caseName } = useSelector((state: any) => state.case);
  const { search } = useLocation();
  const creditorID = new URLSearchParams(search).get("creditorId") || undefined;
  const [showSummary, setShowSummary] = useState(true);
  const creditorName =
    new URLSearchParams(search).get("creditorName") || undefined;
  const [downloadDocument, { isLoading }] = useLazyDownloadDocumentQuery();
  let tableLoadingData = customTableLoadingData;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  const getPaginationObj: any = useMemo(() => {
    let value: any = {};
    if (retainState?.PaymentId === paymentId) {
      value = {
        PageNumber: retainState?.PageNumber || 1,
        PageSize: retainState?.PageSize,
        SearchText: retainState?.SearchText || "",
      };
      if (retainState?.SortOrder && retainState?.SortColumn) {
        value.SortOrder = retainState?.SortOrder;
        value.SortColumn = retainState?.SortColumn;
      }
    } else {
      value = {
        PageNumber: 1,
        SearchText: "",
        PageSize: customPaginationPageSize,
        SortColumn: "ClaimItemReference",
        sortedColumnTypes: { claimIdType: { sort: "asc" } },
      };
    }
    return value;
  }, [retainState, paymentId]);
  const paginationObj = getPaginationObj;
  console.log(paginationObj, "paginationObj");
  const {
    data: paymentSummaryData,
    isLoading: isPymentSummaryLoading,
    error: paymentSummaryError,
  } = useGetPaymentSummaryQuery(paymentId as string, { skip: !paymentId });
  const {
    data: paymentSettlementData,
    isFetching: isSettlementLoading,
    error: SettlementListError,
    status: settlementStatus,
  } = useGetPaymentSettlementListQuery({
    PaymentId: paymentId,
    ...paginationObj,
  });
  const breadcrumbItems =
    creditorID && creditorName
      ? [
          {
            label: "Back",
            elementKey: "Back",
            text: caseName,
            url: `/case/${caseId}/?activeTab=${ActiveTabsOrder.CREDITOR}`,
            icon: <MotifIcon src={IconoirArrowLeft} />,
          },
          {
            label: "Back",
            elementKey: "Back",
            text: creditorName,
            url: `/case/${caseId}/creditor/${creditorID}?activeTab=3`,
            icon: <MotifIcon src={IconoirArrowLeft} />,
          },
          {
            label: "Back",
            elementKey: "Back",
            text: paymentSummaryData?.paymentReference.toString(),
            url: `/case/${caseId}/?activeTab=${ActiveTabsOrder.PAYMENT}`,
            icon: <MotifIcon src={IconoirArrowLeft} />,
            disabled: true,
          },
        ]
      : [
          {
            label: "Back",
            elementKey: "Back",
            text: caseName,
            url: `/case/${caseId}/?activeTab=${ActiveTabsOrder.PAYMENT}`,
            icon: <MotifIcon src={IconoirArrowLeft} />,
          },
          {
            label: "Back",
            elementKey: "Back",
            text: paymentSummaryData?.paymentReference.toString(),
            url: `/case/${caseId}/?activeTab=${ActiveTabsOrder.PAYMENT}`,
            icon: <MotifIcon src={IconoirArrowLeft} />,
            disabled: true,
          },
        ];
  const bannerElements: {
    label: string;
    value: string;
    type?: string;
    display?: boolean;
    elementNode?: ReactNode;
    className?: string;
  }[] = [
    // {
    //   label: t("pages.paymentStatus.paymentSummary.prefix"),
    //   value: paymentSummaryData?.prefix || "-",
    // },
    // {
    //   label: t("pages.paymentStatus.paymentSummary.account"),
    //   value: `${paymentSummaryData?.accountName}`,
    // },
    // {
    //   label: t("pages.paymentStatus.paymentSummary.subAccount"),
    //   value: paymentSummaryData?.subAccountName,
    // },
    {
      label: t("pages.paymentStatus.paymentSummary.issueDate"),
      value: paymentSummaryData?.issueDate
        ? dateFunc(paymentSummaryData?.issueDate).formattedDateOnly
        : "-",
    },
    {
      label: t("pages.paymentStatus.paymentSummary.amount"),
      value: paymentSummaryData
        ? `${
            paymentSummaryData?.decimalPlace
              ? formatAmountWithCommas(
                  paymentSummaryData.amount,
                  paymentSummaryData?.decimalPlace || 0
                )
              : convertToCommaSeparatedNumberwithoutDecimal(
                  paymentSummaryData.amount
                )
          } ${paymentSummaryData.unitName || ""}`
        : "",
      className: "break-word",
    },
    {
      label: t("pages.paymentStatus.paymentSummary.status"),
      value: getPaymentStatus(paymentSummaryData?.paymentStatus),
      type: "HTMLElement",
      elementNode: (
        <div style={{ height: "40px" }}>
          <PaymentStatusColorCode
            paymentStatus={paymentSummaryData?.paymentStatus}
          />
        </div>
      ),
      display: true,
    },
    {
      label: t("pages.paymentStatus.paymentSummary.attachment"),
      value: "image",
      type: "image",
      display: paymentSummaryData?.documentId ? true : false,
      elementNode: (
        <GetCommonDownloadImgButton
          style={{ padding: "0px" }}
          onClick={() => {
            downloadDocument({
              caseId: caseId,
              documentId: paymentSummaryData?.documentId,
              fileName: paymentSummaryData?.fileName,
            });
          }}
          loading={isLoading}
          tooltipText={t("pages.paymentStatus.downloadImageTooltip")}
        ></GetCommonDownloadImgButton>
      ),
    },
  ];
  const tableDataFetching = isSettlementLoading;
  const tableDataError = SettlementListError;
  const tableDataHasRecords = paymentSettlementData?.hasMoreRecords;
  const tableData = paymentSettlementData?.results;
  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "paymentSummaryTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
          PaymentId: paymentId,
        },
      })
    );
  }
  const handleCellClick = (param: any) => {
    navigate(
      `/case/${caseId}/claim/details/` +
        param?.data?.claimItemId +
        `?navId=${paymentId}&navigatedFrom=${
          creditorID && creditorName ? "creditorDistribution" : "paymentGrid"
        }&paymentReference=${paymentSummaryData?.paymentReference?.toString()}&claimId=${
          param?.data?.claimId
        }&creditorName=${param?.data?.creditorName}&updatedOn=${
          param?.data?.updatedOn
        }&claimReference=${param?.data?.claimReference}&creditorId=${
          param?.data?.creditorId
        }`,
      {
        state: {
          paymentId,
        },
      }
    );
  };
  return (
    <>
      <div>
        <Breadcrumb items={breadcrumbItems} ariaLabel="Breadcrumb navigation" />
      </div>
      {isPymentSummaryLoading ? (
        <MotifProgressLoader show variant="default" />
      ) : paymentSummaryError ? (
        <MotifErrorMessage>
          {t("global.globalNotifications.wentWrong")}
        </MotifErrorMessage>
      ) : (
        <Banner
          bannerName={t("pages.paymentStatus.paymentSummary.paymentReference")}
          bannerNameValue={paymentSummaryData?.paymentReference.toString()}
          elements={bannerElements}
          // headerClassName="pad-r-10 banner-title"
          // childClassName="pad-l-30 "
        />
      )}
      <div className="motif-table-title-bar mar-t-20 ">
        <div className="motif-table-info">
          <span className="motif-table-title">
            {t("pages.paymentStatus.paymentSummary.summaryTitle")}
          </span>
          <span className="motif-table-description">
            {t("pages.paymentStatus.paymentSummary.summaryDescription")}
          </span>
        </div>
        <div
          onClick={() => {
            setShowSummary((prev) => !prev);
          }}
          style={{ cursor: "pointer" }}
        >
          <MotifIcon
            aria-label="Dropdown icon"
            fill="none"
            size="24px"
            iconFunction={showSummary ? IconoirNavArrowUp : IconoirNavArrowDown}
          />
        </div>
      </div>

      {showSummary && (
        <div className="paymentSummaryDetailsCon">
          <div
            style={{ rowGap: "20px" }}
            className="motif-row pad-b-20 pad-t-10 pad-l-10 pad-r-10"
          >
            <div className="motif-col-lg-3">
              <div className="title">
                {t("pages.paymentStatus.paymentSummary.prefix")}
              </div>
              <div className="motif-body2-default-bold overFlowWrapBreakWord">
                {paymentSummaryData?.prefix || "-"}
              </div>
            </div>
            <div className="motif-col-lg-3">
              <div className="title">
                {t("pages.paymentStatus.paymentSummary.payeeName")}
              </div>
              <div className="motif-body2-default-bold overFlowWrapBreakWord">
                {paymentSummaryData?.payeeName || "-"}
              </div>
            </div>
            <div className="motif-col-lg-3">
              <div className="title">
                {t("pages.paymentStatus.paymentSummary.account")}
              </div>
              <div className="motif-body2-default-bold overFlowWrapBreakWord">
                {paymentSummaryData?.accountName || "-"}
              </div>
            </div>
            <div className="motif-col-lg-3">
              <div className="title">
                {t("pages.paymentStatus.paymentSummary.subAccount")}
              </div>
              <div className="motif-body2-default-bold overFlowWrapBreakWord">
                {paymentSummaryData?.subAccountName || "-"}
              </div>
            </div>
            <div className="motif-col-lg-3">
              <div className="title">
                {t("pages.paymentStatus.paymentSummary.issueDate")}
              </div>
              <div className="motif-body2-default-bold overFlowWrapBreakWord">
                {dateFunc(paymentSummaryData?.issueDate).formattedDateOnly ||
                  "-"}
              </div>
            </div>
            <div className="motif-col-lg-3">
              <div className="title">
                {t("pages.paymentStatus.paymentSummary.amount")}
              </div>
              <div className="motif-body2-default-bold overFlowWrapBreakWord">
                {paymentSummaryData
                  ? `${
                      paymentSummaryData?.decimalPlace
                        ? formatAmountWithCommas(
                            paymentSummaryData.amount,
                            paymentSummaryData?.decimalPlace || 0
                          )
                        : convertToCommaSeparatedNumberwithoutDecimal(
                            paymentSummaryData.amount
                          )
                    } ${paymentSummaryData.unitName || ""}`
                  : "-"}
              </div>
            </div>
            <div className="motif-col-lg-3">
              <div className="title">
                {t("pages.paymentStatus.paymentSummary.status")}
              </div>
              <div className="overFlowWrapBreakWord">
                <>
                  <PaymentStatusColorCode
                    paymentStatus={paymentSummaryData?.paymentStatus}
                  />
                </>
              </div>
            </div>
            {/* {paymentSummaryData?.documentId && (
              <div className="motif-col-lg-3">
          
                <div className="motif-body2-default-bold overFlowWrapBreakWord">
                  <GetCommonDownloadImgButton
                    onClick={() => {
                      downloadDocument({
                        caseId: caseId,
                        documentId: paymentSummaryData?.documentId,
                        fileName: paymentSummaryData?.fileName,
                      });
                    }}
                    loading={isLoading}
                    tooltipText={t("pages.paymentStatus.downloadImageTooltip")}
                  ></GetCommonDownloadImgButton>
                </div>
              </div>
            )} */}
          </div>
        </div>
      )}
      <div className="mar-t-20">
        <Table
          columnDefs={usePaymentSummaryColumnDefs(
            handleCellClick,
            paymentSummaryData?.decimalPlace,
            paymentSummaryData?.unitName,
            paymentSummaryData?.accountUnitName
          )}
          paginationObj={paginationObj}
          columnTypes={
            retainState?.PaymentId === paymentId
              ? { ...retainState.sortedColumnTypes }
              : { claimIdType: { sort: "asc" } }
          }
          handlePaginationData={handlePaginationData}
          cellRenderers={tableDataFetching ? [] : tableData ? tableData : []}
          TitleComplement={() => <></>}
          title={t("pages.paymentStatus.paymentSummary.tableTitle")}
          description={t("pages.paymentStatus.paymentSummary.tableDescription")}
          overlayNoRowsTemplate={
            tableDataFetching
              ? t(tableLoadingData)
              : tableDataError
              ? t("pages.paymentStatus.somethingWentWrong")
              : t("pages.paymentStatus.noRecord")
          }
          pagination={false}
          pinnedBottomRowData={[
            {
              className: "Total",
              entitlementAmount: null,
              priorSettledAmount: null,
              settlementAmount: null,
            },
          ]}
        />
        <CustomPagination
          apiData={paymentSettlementData}
          apiStatus={settlementStatus}
          currentpage={paginationObj.PageNumber}
          pageSize={paginationObj.PageSize}
          disableNext={!tableDataHasRecords}
          onChangePageSize={(page: number) => {
            dispatch(
              setComponentState1({
                entityName: "paymentSummaryTabState",
                entityState: {
                  PageSize: page,
                  PageNumber: 1,
                  PaymentId: paymentId,
                },
              })
            );
          }}
          onChangeNextPage={() => {
            dispatch(
              setComponentState1({
                entityName: "paymentSummaryTabState",
                entityState: {
                  PageNumber: paginationObj.PageNumber + 1,

                  PaymentId: paymentId,
                },
              })
            );
          }}
          onChangePreviousPage={() => {
            dispatch(
              setComponentState1({
                entityName: "paymentSummaryTabState",
                entityState: {
                  PageNumber: paginationObj.PageNumber - 1,

                  PaymentId: paymentId,
                },
              })
            );
          }}
          pageSizeSelector={paginationPageSizeSelector}
        />
      </div>
    </>
  );
};
export default PaymentSummary;
